import { handleApiResult } from '@store/error';
import { apiSlice } from './apiSlice';

type Status = 'ok' | 'failed';
type ConnectionStatus = 'connected' | 'disconnected';

interface ServerStatus {
  status: Status;
  timestamp: string;
  database: {
    status: ConnectionStatus;
  }
  storage: {
    status: ConnectionStatus;
  }
  process: {
    uptime: string;
    responseTime: string;
    memory: {
      rss: string;
      heapTotal: string;
      heapUsed: string;
    }
  }
  system: {
    hostname: string;
    arch: string;
    memory: {
      total: string;
      free: string;
    }
    cpu: {
      loadAverage: number[];
      cores: number;
    }
    os: {
      platform: NodeJS.Platform;
      release: string;
      uptime: string;
    }
  }
}

export const allowedAuthMethods = ['username_password'] as const;
export type AllowedMethods = typeof allowedAuthMethods[number];

interface Config {
  auth: {
    allowedMethods: AllowedMethods[];
    sessionExpiresInSeconds: number;
  };
  /**
   * What the server records about devices. The two behaviours that are not here are not
   * omissions: the device identifier is always salted and the device name is never stored, and
   * neither is a switch.
   */
  logging: {
    retentionDays: number;
    recordClientIp: boolean;
  };
  api: {
    version: number;
    versionString: string;
  };
  /**
   * True while this installation has no administrator. It is the one thing an unclaimed server
   * says about itself, and the router reads it to decide whether to serve the setup screen
   * instead of the sign-in one.
   */
  setupRequired: boolean;
}

export const serverApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getStatus: builder.query<ServerStatus, void>({
      query: () => '/health',
      onQueryStarted: handleApiResult(),
    }),
    getConfig: builder.query<Config, void>({
      query: () => '/config',
      providesTags: ['Config'],
      onQueryStarted: handleApiResult(),
    }),
    // Named fields rather than Omit<Config, 'api'>: the response also carries `setupRequired`,
    // which is computed from whether a user row exists and is not something a caller may set.
    updateConfig: builder.mutation<void, Pick<Config, 'auth' | 'logging'>>({
      query: (config) => ({ url: '/config', method: 'PUT', body: config }),
      invalidatesTags: ['Config'],
      onQueryStarted: handleApiResult('Server configuration updated.'),
    }),
  }),
});

export const { useGetStatusQuery, useGetConfigQuery, useUpdateConfigMutation } = serverApiSlice;
